import { useState, useEffect, useRef, useCallback, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";
import { useSession } from "@/hooks/use-auth";
import { KomoranIcon } from "@/components/layout/komoran-logo";

function useScramble(text: string) {
  const [display, setDisplay] = useState("");
  const frameRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const decode = useCallback(() => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&";
    let resolved = 0;
    let tick = 0;

    const step = () => {
      tick++;
      const arr: string[] = [];
      for (let i = 0; i < text.length; i++) {
        if (i < resolved) arr.push(text[i]!);
        else arr.push(chars[Math.floor(Math.random() * chars.length)]!);
      }
      if (tick % 4 === 0 && resolved < text.length) resolved++;
      setDisplay(arr.join(""));
      if (resolved <= text.length) frameRef.current = setTimeout(step, 60);
    };
    step();
  }, [text]);

  useEffect(() => {
    const t = setTimeout(decode, 300);
    return () => { clearTimeout(t); clearTimeout(frameRef.current); };
  }, [decode]);

  return display;
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const title = useScramble("KOMORAN");
  const { user, isLoading: sessionLoading } = useSession();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!sessionLoading && user) {
      navigate({ to: "/orders", replace: true });
    }
  }, [user, sessionLoading, navigate]);

  if (sessionLoading || user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a]">
        <p className="text-xs text-white/40">Učitavanje...</p>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const result = await authClient.signIn.email({ email: normalizedEmail, password });
      if (result.error) {
        const status = result.error.status;
        if (status === 401 || status === 400) {
          setError("Pogrešan email ili lozinka");
        } else if (status === 429) {
          setError("Previše pokušaja, pokušajte za nekoliko minuta");
        } else {
          setError("Greška servera, pokušajte ponovo");
        }
      }
    } catch {
      setError("Greška mreže, provjerite konekciju");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[#0a0a0a] overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: "radial-gradient(circle at 50% 40%, rgba(255,255,255,.8), transparent 60%)",
        }}
      />

      <div
        className={`relative z-10 flex flex-col items-center w-full max-w-xs transition-all duration-700 ease-out ${
          mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="relative mb-8">
          <div
            className={`absolute -inset-6 bg-white/[0.02] border border-white/[0.06] transition-all duration-1000 delay-200 ${
              mounted ? "opacity-100 scale-100" : "opacity-0 scale-90"
            }`}
          />
          <KomoranIcon className="relative h-14 w-14 text-white/90" />
        </div>

        <h1 className="text-lg tracking-[0.3em] text-white/90 font-medium mb-1 tabular-nums">
          {title}
        </h1>
        <p className="text-[10px] tracking-[0.2em] uppercase text-white/30 mb-10">
          Skladišni sistem
        </p>

        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
          {error && (
            <div className="border border-red-500/30 bg-red-500/5 px-3 py-2">
              <p className="text-[11px] text-red-400 text-center">{error}</p>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label htmlFor="email" className="text-[10px] tracking-[0.15em] uppercase text-white/40">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vas@email.com"
              required
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="email"
              inputMode="email"
              className="h-10 w-full bg-white/[0.04] border border-white/[0.08] px-3 text-xs text-white/90 placeholder:text-white/20 font-mono outline-none focus:border-white/25 focus:bg-white/[0.06] transition-all"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="password" className="text-[10px] tracking-[0.15em] uppercase text-white/40">
              Lozinka
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="h-10 w-full bg-white/[0.04] border border-white/[0.08] px-3 text-xs text-white/90 placeholder:text-white/20 font-mono outline-none focus:border-white/25 focus:bg-white/[0.06] transition-all"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-2 h-10 w-full bg-white text-[#0a0a0a] text-xs font-medium tracking-[0.1em] uppercase hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.99]"
          >
            {loading ? "Prijava..." : "Prijavi se"}
          </button>
        </form>

        <div className="mt-12 flex items-center gap-3 text-white/15">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-[9px] tracking-[0.2em] uppercase">v2</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>
      </div>
    </div>
  );
}
